import { LinkCard } from '@/components';
import { useSparkColor } from '@/hooks';
import {
  CurrencyValue,
  Flex,
  FlexProps,
  NetworkIcon,
  ProtocolIcon,
  RelativeDateTime
} from '@blockanalitica/ui';
import _ from 'lodash';
import { useTheme } from 'styled-components';

export type SmartActivityData = {
  datetime: string;
  order_index: string;
  wallet_address: string;
  address: string;
  operation: string;
  sender: string;
  sender_label: string | null;
  receiver_label: string | null;
  receiver: string;
  assets: string;
  asset_symbol: string;
  protocol: string;
  network: string;
  name: string;
  block_number: number;
  tx_hash: string;
  event: string;
  amount: string;
  symbol: string;
  index: string;
  asset: string;
  message: string;
};

interface SmartActivityListProps extends FlexProps {
  data: SmartActivityData[];
}

export default function SmartActivityList({
  data,
  ...rest
}: SmartActivityListProps) {
  const hoverColor = useSparkColor();
  const colors = useTheme().colors;

  const formatTransaction = (event: SmartActivityData) => {
    let walletLabel =
      event.operation === 'withdrawal'
        ? event.sender_label || event.sender
        : event.receiver_label || event.receiver;

    const amount = Math.abs(Number(event.assets));
    const assetSymbol = event.asset_symbol;
    const protocol = _.capitalize(event.protocol);
    const network = _.capitalize(event.network);
    const vaultName = event.name;

    if (walletLabel === 'ALM Proxy') {
      walletLabel = 'SLL';
    }

    switch (event.operation) {
      case 'deposit':
        return (
          <>
            {walletLabel} deposited
            <CurrencyValue value={amount} color={colors[event.operation]} />
            {assetSymbol} to {vaultName} on {protocol} via {network}
          </>
        );
      case 'withdrawal':
        return (
          <>
            {walletLabel} withdrew
            <CurrencyValue value={amount} color={colors[event.operation]} />
            {assetSymbol} from {vaultName} on {protocol} via {network}
          </>
        );
      case 'transfer':
        return (
          <>
            {walletLabel} transferred
            <CurrencyValue value={amount} color={colors[event.operation]} />
            {assetSymbol} to {vaultName} on {protocol} via {network}
          </>
        );
      default:
        return (
          <>
            {walletLabel} {event.operation} <CurrencyValue value={amount} />
            {assetSymbol} to {vaultName} on {protocol} via {network}
          </>
        );
    }
  };

  return (
    <Flex flexDirection="column" gap="xs" {...rest}>
      {data.map((row) => (
        <LinkCard
          key={row.order_index}
          to={`/spark-liquidity-layer/${row.network}/${row.wallet_address}/${row.address}`}
          hoverBorderColor={hoverColor}
          flexDirection="row"
          gap="s"
          justifyContent="space-between">
          <Flex flexDirection="row" gap="s" alignItems="center">
            <Flex alignItems="center" gap="3xs">
              <NetworkIcon network={row.network} size="2" />
              <ProtocolIcon protocol={row.protocol} size="2" />
            </Flex>
            <Flex flexDirection="row" gap="3xs" flexWrap="wrap">
              {formatTransaction(row)}
            </Flex>
          </Flex>

          <RelativeDateTime value={row.datetime} />
        </LinkCard>
      ))}
    </Flex>
  );
}
